import api from '../../utils/api';

export interface MasterAdminNotificationRow {
  id: string;
  title: string;
  body: string;
  type: string;
  data: Record<string, unknown> | null;
  emailSent: boolean;
  fcmSent: boolean;
  emailError: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsListResponse {
  success: boolean;
  data: {
    notifications: MasterAdminNotificationRow[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

export async function fetchNotifications(params?: {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
  service?: string;
}): Promise<NotificationsListResponse> {
  const { page = 1, limit = 20, unreadOnly = false, service = 'all' } = params || {};
  const response = await api.get<NotificationsListResponse>('/master-admin/notifications', {
    params: { page, limit, unreadOnly: unreadOnly ? '1' : '0', service },
  });
  return response.data;
}

export async function fetchUnreadCount(): Promise<number> {
  const response = await api.get<{ success: boolean; data: { unreadCount: number } }>(
    '/master-admin/notifications/unread-count'
  );
  return response.data.data.unreadCount;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.patch(`/master-admin/notifications/${id}/read`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.patch('/master-admin/notifications/read-all');
}

export async function registerAdminFcmToken(body: {
  fcmToken: string;
  deviceId: string;
  platform: 'android' | 'ios' | 'web';
}): Promise<void> {
  console.log('[Admin FCM API] POST /master-admin/notifications/fcm-token', {
    deviceId: body.deviceId,
    platform: body.platform,
    tokenPreview: body.fcmToken?.slice(0, 18),
  });
  const response = await api.post('/master-admin/notifications/fcm-token', body);
  console.log('[Admin FCM API] register response', response?.data);
}

export async function deleteAdminFcmToken(deviceId: string): Promise<void> {
  console.log('[Admin FCM API] DELETE /master-admin/notifications/fcm-token/:deviceId', { deviceId });
  const response = await api.delete(
    `/master-admin/notifications/fcm-token/${encodeURIComponent(deviceId)}`
  );
  console.log('[Admin FCM API] delete response', response?.data);
}

export interface RecipientNotificationItem {
  notificationId: string;
  userId?: string | null;
  vendorId?: string | null;
  driverId?: string | null;
  title: string;
  body: string;
  type?: string | null;
  data?: Record<string, unknown> | null;
  isRead: boolean;
  broadcastToAllUsers?: boolean;
  broadcastToAllVendors?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RecipientNotificationResponse {
  success: boolean;
  data: {
    notifications: RecipientNotificationItem[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  };
}

export async function fetchUserNotifications(
  userId: string,
  params?: { page?: number; limit?: number; unreadOnly?: boolean }
): Promise<RecipientNotificationResponse> {
  const { page = 1, limit = 20, unreadOnly = false } = params || {};
  const response = await api.get<RecipientNotificationResponse>(
    `/notifications/user/${encodeURIComponent(userId)}`,
    {
      params: { page, limit, unreadOnly: unreadOnly ? 'true' : 'false' },
    }
  );
  return response.data;
}

export async function fetchVendorNotifications(
  vendorId: string,
  params?: { page?: number; limit?: number; unreadOnly?: boolean }
): Promise<RecipientNotificationResponse> {
  const { page = 1, limit = 20, unreadOnly = false } = params || {};
  const response = await api.get<RecipientNotificationResponse>(
    `/notifications/vendor/${encodeURIComponent(vendorId)}`,
    {
      params: { page, limit, unreadOnly: unreadOnly ? 'true' : 'false' },
    }
  );
  return response.data;
}
